import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./OnboardingOrganisation.module.scss";
import { Button, CircularProgress } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { fetcher } from "../../utils/fetcher";
import { AuthContext } from "../../context/AuthContext";
import { OnboardingOrganisationFirstStep } from "./OnboardingOrganisationFirstStep";
import { OnboardingOrganisationSecondStep } from "./OnboardingOrganisationSecondStep";
import { DuplicateEmailNotice } from "../DuplicateEmailNotice/DuplicateEmailNotice";
import { FileWithPreview } from "../../types/common";

interface Step {
  step: number;
  title: string;
  desc: string;
}

const steps: Step[] = [
  {
    step: 0,
    title: "Základní údaje",
    desc: "Vyplňte základní informace o vaší organizaci.",
  },
  {
    step: 1,
    title: "Doplňující informace",
    desc: "Ať můžeme uživatelům nabídnout vaše akce co nejpřesněji, potřebujeme od vás ještě pár věcí.",
  },
];

interface FormData {
  areas: string[];
  organisation_info: string;
}

export const OnboardingOrganisation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const { currentUser, loading } = authContext;

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    areas: [],
    organisation_info: "",
  });
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [error, setError] = useState<string | false>(false);
  const [duplicateEmail, setDuplicateEmail] = useState(false);

  useEffect(() => {
    if (currentUser) {
      const userData = currentUser.userData as any;
      setCurrentStep(userData?.step_of_registration || 0);
      setFormData({
        areas: (userData?.areas || []).map((id: number) => String(id)),
        organisation_info: userData?.organisation_info || "",
      });
    }
  }, [currentUser]);

  const handleCheckboxToggle = (value: string, field: "areas") => {
    const strValue = String(value);
    const alreadyExists = formData[field]?.includes(strValue);
    if (alreadyExists) {
      setFormData({
        ...formData,
        [field]: formData[field].filter((item) => item !== strValue),
      });
    } else {
      setFormData({
        ...formData,
        [field]: [...formData[field], strValue],
      });
    }
  };

  const minTextLength = 100;
  const remainingCharacters = Math.max(
    0,
    minTextLength - formData.organisation_info.length,
  );

  const renderStep = (minTextLength: number, remainingCharacters: number) => {
    switch (currentStep) {
      case 0:
        return (
          <OnboardingOrganisationFirstStep
            title={steps[currentStep]?.title || ""}
            desc={steps[currentStep]?.desc || ""}
            onSetError={setError}
            onDuplicateEmail={() => setDuplicateEmail(true)}
          />
        );
      case 1:
        return (
          <OnboardingOrganisationSecondStep
            title={steps[currentStep]?.title || ""}
            desc={steps[currentStep]?.desc || ""}
            formData={formData}
            onCheckboxToggle={handleCheckboxToggle}
            onSetFormData={setFormData}
            minTextLength={minTextLength}
            remainingCharacters={remainingCharacters}
            files={files}
            onSetFiles={setFiles}
          />
        );
      default:
        return null;
    }
  };

  const handleClickNext = async () => {
    if (!currentUser) return;

    const updatedData = {
      ...formData,
      // areas are already strings, backend expects strings
      step_of_registration: currentStep + 1,
    };

    try {
      if (files.length > 0) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", files[0]);

        await fetcher({
          url: `/organisation/upload-picture/${currentUser.userId}`,
          method: "POST",
          body: formDataUpload,
          noContentType: true,
        });
      }

      const data = await fetcher<any>({
        url: `/organisation/${currentUser.userId}`,
        method: "PUT",
        body: JSON.stringify(updatedData),
      });

      setCurrentStep(data?.step_of_registration);
      navigate("/organisation", { state: { from: location.pathname } });
    } catch (error) {
      setError(
        "Došlo k chybě při registraci organizace nebo při nahrávání loga, zkuste to prosím později.",
      );
      console.error("Error creating new organisation:", error);
    }
  };

  const isValid =
    formData.areas.length >= 3 &&
    formData.organisation_info.length >= minTextLength;

  if (loading)
    return (
      <div className={styles.onboarding}>
        <CircularProgress />
      </div>
    );

  return (
    <div className={styles.onboarding}>
      <div className={styles.header}>
        {currentStep === 0 && (
          <Button variant="text" onClick={() => navigate("/register")}>
            <ArrowBackIosIcon />
            Zpět
          </Button>
        )}
        <div className={styles.navigation}>
          <span className={styles.highligted}>Krok {currentStep + 1}/2</span>
          <br />
          {steps[currentStep]?.title}
        </div>
      </div>
      {duplicateEmail ? (
        <DuplicateEmailNotice />
      ) : error ? (
        <div className={styles.content}>{error}</div>
      ) : (
        renderStep(minTextLength, remainingCharacters)
      )}
      {!error && !duplicateEmail && currentStep > 0 && (
        <Button
          variant="contained"
          size="large"
          onClick={handleClickNext}
          disabled={!isValid}
        >
          Dokončit registraci
        </Button>
      )}
    </div>
  );
};
